import {
  html,
  attr,
  prop,
  Signal,
  When,
  Fragment,
  computedOf,
  NotEmpty,
} from '@tempots/dom'
import { Anchor, NavigationService } from '@tempots/ui'
import {
  Button,
  Icon,
  Modal,
  InputWrapper,
  TextInput,
  EditableText,
} from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { DataTable } from '@/components/data'
import { EmptyState, StatusBadge } from '@/components/ui'
import { projects as projectsService } from '@/services/gateway'
import { Workspace } from '@/state/workspace'
import { mapProjectStatusToVariant } from '@/utils'
import { Project } from '@/services'

// ============================================================================
// Types
// ============================================================================

export interface ProjectsPageOptions {
  orgId: Signal<string>
}

// ============================================================================
// Projects Page Component
// ============================================================================

export function ProjectsPage({ orgId }: ProjectsPageOptions) {
  orgId.on(Workspace.changeOrganization)

  const projects = computedOf(
    orgId,
    Workspace.projects
  )((id, projs) => {
    return projs[id] ?? []
  })
  // const isLoading = prop(true)
  // const error = prop<string | null>(null)

  // Form state for create modal
  const newProjectName = prop('')
  const newProjectDescription = prop('')
  const isCreating = prop(false)
  const createError = prop<string | null>(null)

  // Get current org name for display
  const currentOrgName = Workspace.organization.$.name

  async function handleCreateProject(close: () => void) {
    const name = newProjectName.get().trim()
    if (!name) {
      createError.set('Project name is required')
      return
    }

    const targetOrgId = orgId.value

    isCreating.set(true)
    createError.set(null)

    try {
      const description = newProjectDescription.get().trim() || undefined
      const newProject = await projectsService.createProject(
        targetOrgId,
        name,
        description
      )

      Workspace.addProject(targetOrgId, newProject)

      // Reset form and close
      newProjectName.set('')
      newProjectDescription.set('')
      close()

      // Navigate to the new project's dashboard
      NavigationService.navigate(`/p/${newProject.id}`)
    } catch (err) {
      createError.set(
        err instanceof Error ? err.message : 'Failed to create project'
      )
    } finally {
      isCreating.set(false)
    }
  }

  // Handle updating project name
  async function handleUpdateProjectName(projectId: string, newName: string) {
    const trimmedName = newName.trim()
    if (!trimmedName) return

    try {
      projectsService.updateProject(projectId, trimmedName)
      Workspace.updateProjectName(projectId, trimmedName)
    } catch (err) {
      console.error(
        err instanceof Error ? err.message : 'Failed to update project name'
      )
    }
  }

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-2'),
      'Projects',
      When(
        currentOrgName.map((name) => name !== null),
        () =>
          html.span(
            attr.class('text-gray-400 text-lg font-normal'),
            currentOrgName.map((name) => ` - ${name}`)
          )
      )
    ),
    content: Fragment(
      // Header with create button
      html.div(
        attr.class(
          'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'
        ),
        html.div(
          html.p(
            attr.class('text-sm text-gray-600'),
            'Organize your work with projects. Each project has its own API keys and members.'
          )
        ),
        Modal({ size: 'md', dismissable: true }, (open, close) =>
          html.div(
            Button(
              {
                onClick: () => {
                  newProjectName.set('')
                  newProjectDescription.set('')
                  createError.set(null)
                  open({
                    header: html.h2(
                      attr.class('text-lg font-semibold text-gray-900'),
                      'Create Project'
                    ),
                    body: html.div(
                      attr.class('space-y-4'),
                      html.p(
                        attr.class('text-sm text-gray-600'),
                        'Create a new project to organize your API keys and resources.'
                      ),
                      InputWrapper({
                        label: 'Project Name',
                        required: true,
                        content: TextInput({
                          value: newProjectName,
                          placeholder: 'My Project',
                          onInput: (v) => newProjectName.set(v),
                        }),
                        hasError: createError.map((e) => e !== null),
                        error: createError,
                      }),
                      InputWrapper({
                        label: 'Description',
                        content: TextInput({
                          value: newProjectDescription,
                          placeholder: 'Optional description...',
                          onInput: (v) => newProjectDescription.set(v),
                        }),
                      })
                    ),
                    footer: html.div(
                      attr.class('flex justify-end gap-3'),
                      Button({ variant: 'outline', onClick: close }, 'Cancel'),
                      Button(
                        {
                          disabled: isCreating,
                          onClick: () => handleCreateProject(close),
                        },
                        isCreating.map((c) =>
                          c ? 'Creating...' : 'Create Project'
                        )
                      )
                    ),
                  })
                },
              },
              Icon({ icon: 'mdi:plus' }, attr.class('w-4 h-4 mr-2')),
              'Create Project'
            )
          )
        )
      ),
      // Table/Cards (only when loaded)
      NotEmpty(
        projects,
        () =>
          html.div(
            attr.class(
              'bg-white rounded-xl border border-gray-200 overflow-hidden'
            ),
            DataTable<Project>({
              columns: [
                {
                  key: 'name',
                  header: 'Name',
                  render: (item) =>
                    html.div(
                      attr.class('flex items-center gap-3'),
                      html.div(
                        attr.class(
                          item.map((p) =>
                            p.status === 'archived'
                              ? 'w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 text-gray-400'
                              : 'w-10 h-10 rounded-lg flex items-center justify-center bg-brand-100 text-brand-600'
                          )
                        ),
                        Icon({ icon: 'mdi:folder' }, attr.class('w-5 h-5'))
                      ),
                      html.div(
                        html.div(
                          attr.class('flex items-center gap-2'),
                          html.div(
                            attr.class('font-medium text-gray-900'),
                            EditableText({
                              value: item.map((p) => p.name),
                              onChange: (newName) => {
                                const projectId = item.get().id
                                handleUpdateProjectName(projectId, newName)
                              },
                              placeholder: 'Project name',
                            })
                          ),
                          When(
                            item.map((p) => p.isDefault),
                            () =>
                              html.span(
                                attr.class(
                                  'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'
                                ),
                                'Default'
                              )
                          ),
                          When(
                            item.map((p) => p.status === 'archived'),
                            () =>
                              html.span(
                                attr.class(
                                  'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600'
                                ),
                                'Archived'
                              )
                          )
                        ),
                        html.p(
                          attr.class('text-sm text-gray-500'),
                          item.map((p) => p.description || 'No description')
                        )
                      )
                    ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (item) =>
                    StatusBadge({
                      label: item.map((p): string =>
                        p.status === 'active' ? 'Active' : 'Archived'
                      ),
                      variant: item.map((p) =>
                        mapProjectStatusToVariant(p.status)
                      ),
                    }),
                },
                {
                  key: 'id',
                  header: '',
                  render: (item) =>
                    Anchor(
                      { href: item.map((p) => `/p/${p.id}`) },
                      attr.class(
                        'text-brand-600 hover:text-brand-700 text-sm font-medium'
                      ),
                      'Go to'
                    ),
                },
              ],
              data: projects,
              renderMobileCard: (project) => ProjectCard(project),
              emptyMessage: 'No projects yet. Create one to get started.',
            })
          ),
        () =>
          EmptyState({
            iconName: 'mdi:folder-open',
            title: 'No projects yet',
            description:
              'Create your first project to organize your API keys and team members.',
            action: Modal({ size: 'md', dismissable: true }, (open, close) =>
              Button(
                {
                  onClick: () => {
                    newProjectName.set('')
                    newProjectDescription.set('')
                    createError.set(null)
                    open({
                      header: html.h2(
                        attr.class('text-lg font-semibold text-gray-900'),
                        'Create Project'
                      ),
                      body: html.div(
                        attr.class('space-y-4'),
                        InputWrapper({
                          label: 'Project Name',
                          required: true,
                          content: TextInput({
                            value: newProjectName,
                            placeholder: 'My Project',
                            onInput: (v) => newProjectName.set(v),
                          }),
                          hasError: createError.map((e) => e !== null),
                          error: createError,
                        }),
                        InputWrapper({
                          label: 'Description',
                          content: TextInput({
                            value: newProjectDescription,
                            placeholder: 'Optional description...',
                            onInput: (v) => newProjectDescription.set(v),
                          }),
                        })
                      ),
                      footer: html.div(
                        attr.class('flex justify-end gap-3'),
                        Button(
                          { variant: 'outline', onClick: close },
                          'Cancel'
                        ),
                        Button(
                          {
                            disabled: isCreating,
                            onClick: () => handleCreateProject(close),
                          },
                          isCreating.map((c) =>
                            c ? 'Creating...' : 'Create Project'
                          )
                        )
                      ),
                    })
                  },
                },
                Icon({ icon: 'mdi:plus' }, attr.class('w-4 h-4 mr-2')),
                'Create Project'
              )
            ),
          })
      )
    ),
  })
}

// ============================================================================
// Project Card Component (for mobile view)
// ============================================================================

function ProjectCard(project: Signal<Project>) {
  return html.div(
    attr.class('bg-white border border-gray-200 rounded-lg p-4'),
    html.div(
      attr.class('flex items-start justify-between'),
      html.div(
        attr.class('flex items-center gap-3'),
        html.div(
          attr.class(
            project.map((p) =>
              p.status === 'archived'
                ? 'w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 text-gray-400'
                : 'w-10 h-10 rounded-lg flex items-center justify-center bg-brand-100 text-brand-600'
            )
          ),
          Icon({ icon: 'mdi:folder' }, attr.class('w-5 h-5'))
        ),
        html.div(
          html.div(
            attr.class('flex items-center gap-2'),
            html.h3(
              attr.class('font-medium text-gray-900'),
              project.map((p) => p.name)
            ),
            When(
              project.map((p) => p.isDefault),
              () =>
                html.span(
                  attr.class(
                    'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800'
                  ),
                  'Default'
                )
            )
          ),
          html.p(
            attr.class('text-sm text-gray-500 mt-1'),
            project.map((p) => p.description || 'No description')
          )
        )
      ),
      Anchor(
        { href: project.map((p) => `/p/${p.id}`) },
        attr.class('text-brand-600 hover:text-brand-700'),
        Icon({ icon: 'mdi:chevron-right' }, attr.class('w-5 h-5'))
      )
    ),
    html.div(
      attr.class('mt-3 flex items-center gap-2'),
      StatusBadge({
        label: project.map((p): string =>
          p.status === 'active' ? 'Active' : 'Archived'
        ),
        variant: project.map((p) => mapProjectStatusToVariant(p.status)),
      })
    )
  )
}
